import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { axiosGateway } from "../../../api/axios";
import { MESSAGES_PATHS } from "../../../api/endpoints";
import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks";
import { ensureUploadUrl } from "../../../utils/uploads";
import { useSocket } from "../../context/SocketContext";
import ContactsList from "./ContactsList";

const stripId = (v) => (v == null ? "" : String(v).replace(/^DR_/i, ""));

const resolveAvatar = (src) => {
  if (!src) return null;
  return /^(blob:|data:|https?:\/\/)/i.test(src) ? src : ensureUploadUrl(src);
};

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    : d.toLocaleDateString();
};

/* Doctor <-> Doctor contacts (DOCTOR_DOCTOR conversations). */
export function DoctorsContactsList({ meId, onSelectContact, selectedContactId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { subscribe } = useSocket() || {};
  const directory = useSelector((s) => s.doctor?.doctors) || [];

  const [conversations, setConversations] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(null);
  const [openError, setOpenError] = useState(null);
  const refreshTimer = useRef(null);

  const me = stripId(meId);

  useEffect(() => {
    if (!directory.length) dispatch(fetchDoctors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const directoryById = useMemo(() => {
    const map = new Map();
    directory.forEach((d) => map.set(stripId(d.id), d));
    return map;
  }, [directory]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await axiosGateway.get(MESSAGES_PATHS.CONV_DOCTORS, { withCredentials: true });
      setConversations(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch {
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadConversations().finally(() => setLoading(false));
  }, [loadConversations]);

  // Real-time: refresh last message / unread badge whenever a message flows.
  useEffect(() => {
    if (!subscribe) return undefined;
    const refresh = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(loadConversations, 300);
    };
    const offNew = subscribe("newMessage", refresh);
    const offRead = subscribe("readMessage", refresh);
    return () => {
      offNew();
      offRead();
      clearTimeout(refreshTimer.current);
    };
  }, [subscribe, loadConversations]);

  useEffect(() => {
    const refresh = () => loadConversations();
    window.addEventListener("doctor-conversations:refresh", refresh);
    return () => window.removeEventListener("doctor-conversations:refresh", refresh);
  }, [loadConversations]);

  const toContact = useCallback(
    (doctorId, conversationId) => {
      const peerId = stripId(doctorId);
      const d = directoryById.get(peerId);
      const name = d?.fullName || d?.name || `Dr. ${peerId.slice(-6)}`;
      return {
        id: peerId,
        userId: peerId,
        peerId,
        name,
        fullName: name,
        email: d?.email,
        avatar: resolveAvatar(d?.avatar),
        specialty: d?.specialty,
        meta: {
          type: "doctor",
          role: "doctor",
          specialty: d?.specialty || "",
          conversationId,
          displayName: name,
        },
      };
    },
    [directoryById]
  );

  const openDoctor = useCallback(
    async (doctorId) => {
      const peerId = stripId(doctorId);
      if (!peerId || opening) return;
      setOpening(peerId);
      setOpenError(null);
      try {
        const res = await axiosGateway.post(
          MESSAGES_PATHS.CONV_DOCTOR_TO_DOCTOR,
          { doctorId: peerId },
          {
            withCredentials: true,
            allowedRequestFields: ["doctorId"],
          }
        );
        const conv = res.data?.conversation || res.data;
        onSelectContact?.(toContact(peerId, conv?.id || conv?._id));
        setQuery("");
        loadConversations();
      } catch (err) {
        setOpenError(
          err?.response?.status === 403
            ? t("chatWindow.blockedByPeer")
            : err?.response?.data?.message || err.message
        );
      } finally {
        setOpening(null);
      }
    },
    [opening, onSelectContact, toContact, loadConversations, t]
  );

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return [];
    return directory
      .filter((d) => stripId(d.id) !== me)
      .filter((d) =>
        [d.fullName, d.specialty, d.email, d.city].some((f) => String(f || "").toLowerCase().includes(q))
      )
      .slice(0, 20);
  }, [directory, q, me]);

  const rows = q
    ? searchResults.map((d) => ({
        key: `dir-${d.id}`,
        peerId: stripId(d.id),
        name: d.fullName,
        specialty: d.specialty,
        avatar: resolveAvatar(d.avatar),
      }))
    : conversations.map((c) => {
        const peerId = stripId(c.peerId);
        const d = directoryById.get(peerId);
        return {
          key: c.id,
          peerId,
          name: d?.fullName || `Dr. ${peerId.slice(-6)}`,
          specialty: d?.specialty,
          avatar: resolveAvatar(d?.avatar),
          preview: c.lastMessagePreview,
          time: formatTime(c.lastMessageAt),
          unread: peerId === stripId(selectedContactId) ? 0 : c.unreadCount || 0,
          blockedByMe: c.blockedByMe,
          restrictedByMe: c.restrictedByMe,
          blockedByPeer: c.blockedByPeer,
        };
      });

  return (
    <div className="contacts-container">
      <div className="search-container">
        <input
          className="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("messaging.contacts.searchDoctors")}
        />
      </div>

      <div className="contacts-list">
        {openError && <div className="dcl-empty dcl-error">{openError}</div>}
        {!rows.length && !loading && (
          <div className="dcl-empty">
            {q ? t("messaging.contacts.noDoctorResults") : t("messaging.contacts.noDoctorConversations")}
          </div>
        )}

        {rows.map((r) => (
          <button
            type="button"
            key={r.key}
            className={`contact-row dcl-row ${stripId(selectedContactId) === r.peerId ? "selected" : ""}`}
            onClick={() => openDoctor(r.peerId)}
            disabled={opening === r.peerId}
          >
            {r.avatar ? (
              <img className="dcl-avatar" src={r.avatar} alt={r.name} />
            ) : (
              <div className="dcl-avatar dcl-avatar--fallback">{initials(r.name)}</div>
            )}
            <div className="dcl-info">
              <div className="dcl-name">{r.name}</div>
              {(r.blockedByMe || r.blockedByPeer || r.restrictedByMe) && (
                <span className={`dcl-flag ${r.blockedByMe || r.blockedByPeer ? "dcl-flag--blocked" : ""}`}>
                  {r.blockedByMe
                    ? t("chatWindow.block")
                    : r.blockedByPeer
                    ? t("chatWindow.blockedByPeer")
                    : t("chatWindow.restrictedByMe")}
                </span>
              )}
              <div className="dcl-sub">{r.preview || r.specialty || ""}</div>
              {r.preview && r.specialty && <div className="dcl-specialty">{r.specialty}</div>}
            </div>
            <div className="dcl-meta">
              {r.time && <span className="dcl-time">{r.time}</span>}
              {r.unread > 0 && <span className="dcl-badge">{r.unread > 99 ? "99+" : r.unread}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* Tab switch: "Patients messages" | "Doctors messages" in the same panel. */
export default function ContactsTabs({ tab, onTabChange, meId, onSelectContact, selectedContactId }) {
  const { t } = useTranslation();

  return (
    <div className="ct-wrapper">
      <div className="ct-tabs" role="tablist">
        {[
          ["patients", t("messaging.contacts.titlePatients")],
          ["doctors", t("messaging.contacts.titleDoctors")],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`ct-tab ${tab === key ? "ct-tab--active" : ""}`}
            onClick={() => onTabChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "doctors" ? (
        <DoctorsContactsList
          meId={meId}
          onSelectContact={onSelectContact}
          selectedContactId={selectedContactId}
        />
      ) : (
        <ContactsList
          mode="doctor"
          onSelectContact={onSelectContact}
          selectedContactId={selectedContactId}
        />
      )}
    </div>
  );
}
